import React, { ComponentProps, useContext } from 'react';
import { asWidgetProps } from 'Src/types/widget-helpers';
import { classnames } from 'Utils/classnames/classnames.utils';
import { get } from 'lodash';
import moment from 'moment';
import { messageFormat as message } from '@clubmed/utils';
import { minAge, maxAge } from 'Components/utils/common-validator/CommonValidators';
import { ICONS } from 'Components/utils/icon/icons';
import { B2CAppContext } from 'Context/ReactAppContext';
import type { Kids } from 'Store/trip-information/kids/kids.reducer';
import 'Store/trip-information/kids/kids.reducer';
import { MAX_PAX, MAX_PAX_SELLER } from 'Utils/faceting/constants';
import { FormInputTelDate } from 'Components/frameworks/form-input-tel-date/FormInputTelDate';
import { useFormValidator } from 'Components/frameworks/form-validator-hoc/useFormValidator';
import { Icon } from 'Components/frameworks/icon/Icon';
import { InputNumber } from 'Components/frameworks/form-inputs/input-number/InputNumber';
import { DEFAULT_MIN_AGE, DEFAULT_MAX_AGE } from './participants.constants';
type Props = {
  adultAge: number;
  adultsLabel: string;
  adultsNumber: number;
  adultOnly: boolean;
  childAge: number;
  childrenBirthdayDateFormatPlaceholder: string;
  className?: string;
  dateFormat: string;
  dayPlaceholder: string;
  departureDate: string;
  kids: Array<Kids>;
  kidsAgeLimit: string;
  kidsLabel: string;
  minAgeInformation: string;
  monthPlaceholder: string;
  setAdultsNumber: (arg0: number) => void;
  setKids: (arg0: { kids: Array<Kids>; kidsValidations?: Array<unknown> }) => void;
  yearPlaceholder: string;
};
function getFields(props: Props): Array<FormField> {
  const {
    dateFormat,
    kids = [],
    adultAge = DEFAULT_MAX_AGE,
    childAge = DEFAULT_MIN_AGE,
    departureDate,
  } = props;
  return kids.map((kid, idx) => {
    const birthdate = get(kid, 'birthdate');
    const adultAgeInYear = +adultAge;
    const childAgeInYear = +childAge / 12;
    const params = {
      birthdayFormat: dateFormat,
      onDate: departureDate,
      onDateFormat: 'YYYYMMDD',
    };
    return {
      name: `birthdate.${idx}`,
      value: birthdate ? moment(birthdate).format(dateFormat) : '',
      validators: [
        maxAge({
          ...params,
          ageRequired: adultAgeInYear,
        }),
        minAge({
          ...params,
          ageRequired: childAgeInYear,
        }),
      ],
    };
  });
}
export function Participants({ kids = [], ...restProps }: Props) {
  const props = {
    kids,
    ...restProps,
  };
  const {
    _adultAge,
    adultsLabel,
    adultsNumber,
    adultOnly,
    childrenBirthdayDateFormatPlaceholder,
    className,
    dateFormat,
    dayPlaceholder,
    kidsAgeLimit,
    kidsLabel,
    minAgeInformation,
    monthPlaceholder,
    setAdultsNumber,
    setKids,
    yearPlaceholder,
  } = props;
  const context = useContext(B2CAppContext);
  const { fields, validateFields, updateFieldValue } = useFormValidator(getFields, props);
  const getKidBirthdate = (idx: number): string => {
    const birthdate = get(kids[idx], 'birthdate');
    return String(
      get(fields, `${idx}.value`) || (birthdate ? moment(birthdate).format(dateFormat) : ''),
    );
  };
  const onAdultsNumberChange = (_: any, value: number) => {
    setAdultsNumber(value);
  };
  const onKidsNumberChange = (_: any, value: number) => {
    const newKids = Array(value)
      .fill(undefined)
      .map((kid, i) => {
        if (kids[i]) {
          return kids[i];
        }
        return kid;
      });
    const kidsValidations = fields.map(({ validationState }) => validationState);
    setKids({
      kids: newKids,
      kidsValidations,
    });
  };
  const handleKids = (idx: number) => {
    return (value: string) => {
      const kidsValidations = validateFields().map(
        (kidsValidation) => kidsValidation.validationState,
      );
      const newKids = kids.map((kid, index) =>
        idx !== index
          ? kid
          : {
              ...kid,
              birthdate: moment(value, dateFormat).valueOf(),
            },
      );
      setKids({
        kids: newKids,
        kidsValidations,
      });
    };
  };
  const maxPax = context.isSeller ? MAX_PAX_SELLER : MAX_PAX + 1;
  const getMaxAdultsAvailable = () => {
    return maxPax - kids.length;
  };
  const getMaxKidsAvailable = () => {
    return maxPax - adultsNumber;
  };
  return (
    <section className={classnames('grid grid-cols-1 gap-20 md:grid-cols-2 md:gap-40', className)}>
      <div>
        <div className="flex flex-col justify-between gap-10 rounded-12 bg-white p-15 md:inline-flex md:flex-row md:px-40">
          <InputNumber
            {...asWidgetProps<ComponentProps<typeof InputNumber>>({
              dataTestId: 'ParticipantsAdultsSelect',
              label: adultsLabel,
              max: getMaxAdultsAvailable(),
              min: 1,
              onChange: onAdultsNumberChange,
              step: 1,
              value: adultsNumber,
            })}
          />
          {!adultOnly && (
            <InputNumber
              {...asWidgetProps<ComponentProps<typeof InputNumber>>({
                dataTestId: 'ParticipantsChildrenSelect',
                max: getMaxKidsAvailable(),
                label: kidsLabel,
                min: 0,
                notes: kidsAgeLimit,
                onChange: onKidsNumberChange,
                step: 1,
                value: kids.length,
              })}
            />
          )}
        </div>
        {minAgeInformation && kids.length > 0 && (
          <p
            className="mt-10 flex items-center gap-10 text-center font-sans text-10 text-orange md:justify-end"
            data-selector="ParticipantsKidsAgeMessage"
          >
            <Icon
              {...asWidgetProps<ComponentProps<typeof Icon>>({
                name: ICONS.WARNING,
                size: '1rem',
              })}
            />
            {minAgeInformation}
          </p>
        )}
      </div>
      {kids.length > 0 && (
        <ul className="grid grid-cols-1 gap-20 md:grid-cols-2">
          {Array.from({
            length: kids.length,
          }).map((_child, idx) => {
            const label = message(childrenBirthdayDateFormatPlaceholder).format({
              childIndex: `${idx + 1}`,
            });
            const birthdate = getKidBirthdate(idx);
            const { validationState } = get(fields, `${idx}`, {}) as FormField;
            return (
              <li key={idx}>
                <FormInputTelDate
                  {...asWidgetProps<ComponentProps<typeof FormInputTelDate>>({
                    className: 'direction-ltr',
                    dateFormat: dateFormat,
                    dayPlaceholder: dayPlaceholder,
                    id: `birthday${idx}`,
                    isRequired: true,
                    label: label,
                    monthPlaceholder: monthPlaceholder,
                    onBlur: handleKids(idx),
                    onChange: updateFieldValue(`birthdate.${idx}`),
                    validationState: validationState,
                    value: birthdate,
                    yearPlaceholder: yearPlaceholder,
                  })}
                />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
